import React, { useId } from "react";

// Qedara Chat logo — reproduced as inline SVG (no raster).
//   variant="full"  -> mark + wordmark (Qedara white / Chat gradient)
//   variant="icon"  -> mark only
//   variant="mono"  -> solid white mark (for colored / busy backgrounds)
// Gradient + filter ids are unique per instance (useId) so multiple logos
// on the same page never collide.

const RING_PATH =
  "M120 24 C67 24 24 67 24 120 C24 173 67 216 120 216 C173 216 216 173 216 120 " +
  "C216 67 173 24 120 24Z M120 62 C152 62 178 88 178 120 C178 152 152 178 120 178 " +
  "C88 178 62 152 62 120 C62 88 88 62 120 62Z";

const TAIL_PATH =
  "M146 158 C155 166 169 179 186 193 C195 200 202 207 207 216 C193 216 176 215 162 210 " +
  "C149 206 136 197 127 187 C124 184 124 178 128 174 L142 160 C143 159 145 158 146 158Z";

const GRADIENT_CSS =
  "linear-gradient(135deg, #12DFF3 0%, #176CFF 45%, #7B2DFF 75%, #D24BFF 100%)";

const FONT_STACK = "Inter, Satoshi, Manrope, 'Cabinet Grotesk', Arial, sans-serif";

export default function QedaraLogo({
  variant = "full",
  markSize = 140,
  withWordmark,
  className = "",
  style,
}) {
  const uid = useId().replace(/:/g, "");
  const gradId = `qcGrad-${uid}`;
  const hlId = `qcHl-${uid}`;
  const shId = `qcSh-${uid}`;

  const mono = variant === "mono";
  const showWordmark = withWordmark ?? variant === "full";

  const markFill = mono ? "#FFFFFF" : `url(#${gradId})`;
  const bubbleFill = mono ? "transparent" : "#05070D";

  const nameSize = Math.round(markSize * 0.51);
  const chatSize = Math.round(markSize * 0.4);
  const gap = Math.round(markSize * 0.2);

  return (
    <div
      className={`qedara-logo ${className}`}
      style={{ display: "inline-flex", alignItems: "center", gap, ...style }}
    >
      <svg
        width={markSize}
        height={markSize}
        viewBox="0 0 240 240"
        fill="none"
        role="img"
        aria-label="Qedara Chat"
        style={{ flex: "0 0 auto" }}
      >
        <defs>
          <linearGradient
            id={gradId}
            x1="30"
            y1="20"
            x2="210"
            y2="220"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#12DFF3" />
            <stop offset="45%" stopColor="rgb(var(--accent))" />
            <stop offset="75%" stopColor="#7B2DFF" />
            <stop offset="100%" stopColor="#D24BFF" />
          </linearGradient>
          {!mono && (
            <radialGradient id={hlId} cx="35%" cy="20%" r="70%">
              <stop offset="0%" stopColor="#6EFFFF" stopOpacity="0.95" />
              <stop offset="55%" stopColor="rgb(var(--accent))" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#7B2DFF" stopOpacity="0" />
            </radialGradient>
          )}
          <filter id={shId} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="16"
              stdDeviation="16"
              floodColor="#000000"
              floodOpacity="0.45"
            />
          </filter>
        </defs>

        {/* Outer Q ring */}
        <path d={RING_PATH} fill={markFill} fillRule="evenodd" filter={`url(#${shId})`} />
        {/* Gloss / highlight overlay (skipped in mono) */}
        {!mono && (
          <path d={RING_PATH} fill={`url(#${hlId})`} fillRule="evenodd" opacity="0.8" />
        )}
        {/* Q tail / chat pointer */}
        <path d={TAIL_PATH} fill={markFill} filter={`url(#${shId})`} />
        {/* Inner speech bubble */}
        <ellipse cx="120" cy="120" rx="58" ry="44" fill={bubbleFill} />
        {/* Three dots */}
        <circle cx="92" cy="120" r="10" fill="#FFFFFF" />
        <circle cx="120" cy="120" r="10" fill="#FFFFFF" />
        <circle cx="148" cy="120" r="10" fill="#FFFFFF" />
      </svg>

      {showWordmark && (
        <div style={{ lineHeight: 0.9 }}>
          <div
            style={{
              fontFamily: FONT_STACK,
              fontSize: nameSize,
              fontWeight: 850,
              letterSpacing: "-0.055em",
              color: "#FFFFFF",
            }}
          >
            Qedara
          </div>
          <div
            style={{
              marginTop: Math.round(markSize * 0.07),
              fontFamily: FONT_STACK,
              fontSize: chatSize,
              fontWeight: 800,
              letterSpacing: "0.015em",
              ...(mono
                ? { color: "#FFFFFF" }
                : {
                    background: GRADIENT_CSS,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    WebkitTextFillColor: "transparent",
                  }),
            }}
          >
            Chat
          </div>
        </div>
      )}
    </div>
  );
}
